import cn from 'classnames'
import {
  getInvoiceStatusName,
  invoiceStatuses,
  prepareInvoiceStatusOptions,
  selectCurrentLanguage,
  useAppSelector
} from 'mmfintech-backend-api'
import type { InvoiceResponse, SelectOptions } from 'mmfintech-backend-api/types'
import { useEffect, useState } from 'react'
import Select, { type CSSObjectWithLabel, components } from 'react-select'
import './InvoiceStatus.scss'

interface Props {
  id?: string
  name?: string
  value?: InvoiceResponse['status']
  overdue?: boolean
  onChange?: (value: NonNullable<InvoiceResponse['status']>) => void
  className?: string
  disabled?: boolean
}

function InvoiceStatus({ id, name, disabled, value, overdue, onChange, className, ...rest }: Props) {
  const [selectedOption, setSelectedOption] = useState(null)
  const [translatedOptions, setTranslatedOptions] = useState<SelectOptions>([])

  const selectedLanguage = useAppSelector(selectCurrentLanguage)

  function getBackground(status: string) {
    return status === invoiceStatuses.OPENED && overdue
      ? '#F5E3E3' // 'rgba(255, 49, 49, 0.1)'
      : status === invoiceStatuses.PAID
        ? '#e3f2de' // 'rgba(68, 204, 0, 0.1)'
        : status === invoiceStatuses.CANCELED
          ? 'rgba(167, 179, 190, 0.1)'
          : '#fffbeb' // 'rgba(255, 206, 50, 0.1)'
  }

  function getColor(status?: string) {
    return status === invoiceStatuses.OPENED && overdue
      ? '#ff3131'
      : status === invoiceStatuses.PAID
        ? '#44cc00'
        : status === invoiceStatuses.CANCELED
          ? '#a7b3be'
          : '#ffce32'
  }

  useEffect(() => {
    const options =
      value === invoiceStatuses.OPENED
        ? [invoiceStatuses.PAID, invoiceStatuses.CANCELED]
        : value === invoiceStatuses.CANCELED
          ? [invoiceStatuses.OPENED, invoiceStatuses.PAID]
          : []
    setTranslatedOptions(prepareInvoiceStatusOptions(options))

    setSelectedOption({
      ...selectedOption,
      label: getInvoiceStatusName(selectedOption?.value)
    })
    // eslint-disable-next-line
  }, [value, selectedLanguage])

  useEffect(() => {
    setSelectedOption({ value, label: getInvoiceStatusName(value, overdue) })
  }, [value, overdue])

  function handleChange(selectedOption) {
    onChange && onChange(selectedOption?.value)
  }

  const customStyles = {
    control: (base: CSSObjectWithLabel) => ({
      ...base,
      minHeight: '3rem',
      cursor: 'pointer',
      textAlign: 'center' as const,
      borderRadius: '4px',
      border: '1px solid ' + getColor(selectedOption?.value),
      boxShadow: 'none',
      backgroundColor: getBackground(selectedOption?.value),
      color: getColor(selectedOption?.value)
    }),
    valueContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      justifyContent: 'center'
    }),
    singleValue: (base: CSSObjectWithLabel) => ({
      ...base,
      width: '100%',
      color: 'inherit',
      fontSize: '1.2rem',
      fontWeight: '400',
      lineHeight: '1.5rem'
    }),
    menu: (base: CSSObjectWithLabel) => ({
      ...base,
      border: 'none !important',
      backgroundColor: '#ffffff !important'
    }),
    option: (base: CSSObjectWithLabel, { isFocused }: { isFocused: boolean }) => ({
      ...base,
      cursor: 'pointer',
      fontSize: '1.2rem',
      fontWeight: '400',
      color: isFocused ? '#ffffff' : '#000000',
      backgroundColor: isFocused ? '#000000' : 'transparent'
    }),
    dropdownIndicator: (base: CSSObjectWithLabel) => ({
      ...base,
      cursor: 'pointer',
      padding: '0 8px',
      color: getColor(selectedOption?.value)
    })
  }

  function addDataTest(Component, dataTest) {
    return props => <Component {...props} innerProps={Object.assign({}, props.innerProps, { 'data-test': dataTest })} />
  }

  return (
    <div className='invoice-status-wrapper select-wrapper'>
      <Select
        {...rest}
        id={id}
        name={name}
        isDisabled={disabled}
        isSearchable={false}
        className={cn('invoice-status', className)}
        placeholder=''
        options={translatedOptions}
        value={selectedOption}
        styles={customStyles}
        onChange={handleChange}
        components={{
          IndicatorSeparator: () => null,
          Control: addDataTest(components.Control, selectedOption?.value?.toLowerCase())
        }}
        menuPlacement='auto'
      />
    </div>
  )
}

export default InvoiceStatus
